<template>
  <div v-if="mainMap" ref="mapContainer" class="mapwrapper"></div>
</template>

<script>
import { onMounted, ref, nextTick, reactive, toRefs, watch } from "vue";
import { showToast, showSuccessToast, showFailToast } from "vant";
import positionIcon from "@/assets/position.png"; // 备用：旧的当前位置图标
import positionOtherIcon from "@/assets/position_other.png"; // 使用 import 加载本地图标
import myLocationIcon from "@/assets/map-icons/mylocation.png"; // 新的“我的位置”图标
import { useI18n } from "vue-i18n";
import {
  TD_MAP_KEY,
  CHINA_DEFAULT_PROVINCE_ID,
  CHINA_ALL_PROVINCE,
  CHINA_DEFAULT_LOCATION,
} from "@/utils/config";
import {
  getGoogleMapLocation,
  getTdMapLocation,
  getDealerListApi,
  getOpenRetailerListApi,
  getMultiLevelDistrictApi,
} from "@/utils/neturls";

import { getProvinceConfigByLatLng, calcDistanceKm } from "@/utils/geoCity";
import { isInChina } from "@/utils/commonUtils";

/** 从天地图脚本 URL 解析 lang；无参数时官方默认一般为中文资源 */
function parseTiandituLangFromScriptSrc(src) {
  if (!src) return "zh_cn";
  try {
    const u = new URL(
      src,
      typeof location !== "undefined" ? location.href : undefined
    );
    return u.searchParams.get("lang") || "zh_cn";
  } catch {
    return "zh_cn";
  }
}

function normalizeTdApiLang(lang) {
  return lang === "en" ? "en" : "zh_cn";
}

function tdApiLangMatches(loaded, needed) {
  return normalizeTdApiLang(loaded) === normalizeTdApiLang(needed);
}

/** 移除天地图脚本并清理全局，便于按 lang 重新拉取 */
function clearTiandituRuntime() {
  document
    .querySelectorAll('script[src*="api.tianditu.gov.cn/api"]')
    .forEach((el) => {
      el.remove();
    });
  try {
    delete window.T;
  } catch (e) {
    window.T = undefined;
  }
  try {
    delete window.__TIANDITU_API_LANG__;
  } catch (e) {
    window.__TIANDITU_API_LANG__ = undefined;
  }
}

// 定位相关
/** 浏览器 getCurrentPosition 的 timeout（毫秒） */
const GEOLOCATION_API_TIMEOUT_MS = 4000;
/** 少数浏览器不回调时外层兜底，须略大于 API timeout */
const GEOLOCATION_WATCHDOG_MS = GEOLOCATION_API_TIMEOUT_MS + 2000;

const useMapPositionEffect = (emit, mapAlwaysCenter, t) => {
  // 获取用户当前定位
  const getUserLocation = (mapToCenterFunc, mapCenter) => {
    if (!navigator.geolocation) {
      // alert("您的浏览器不支持地理定位功能");
      emit("update:locationPermission", false);
      showFailToast("Your browser does not support geolocation functionality");
      return;
    }

    let geoSettled = false;
    let geoWatchdogId = null;
    const markGeoDone = () => {
      if (geoSettled) return;
      geoSettled = true;
      if (geoWatchdogId != null) {
        clearTimeout(geoWatchdogId);
        geoWatchdogId = null;
      }
    };

    geoWatchdogId = setTimeout(() => {
      if (geoSettled) return;
      geoSettled = true;
      if (geoWatchdogId != null) clearTimeout(geoWatchdogId);
      geoWatchdogId = null;
      console.error("定位失败:", "watchdog timeout");
      showToast(t("app.home.map_geolocation_watchdog_timeout"));
      emit("update:currentLocation", {
        lat: mapCenter.value.lat,
        lng: mapCenter.value.lng,
      });
      emit("update:locationPermission", false);
      setTimeout(() => {
        mapToCenterFunc(
          mapCenter.value.lng,
          mapCenter.value.lat,
          false,
          CHINA_DEFAULT_PROVINCE_ID
        );
        emit("update:city", CHINA_DEFAULT_PROVINCE_ID);
      }, 500);
    }, GEOLOCATION_WATCHDOG_MS);

    const geoOptions = {
      enableHighAccuracy: false,
      timeout: GEOLOCATION_API_TIMEOUT_MS,
      maximumAge: 300000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        markGeoDone();
        const { latitude, longitude } = position.coords;
        console.log("定位的经纬", latitude, longitude);
        console.log("getCurrentPosition", position.coords);
        // alert("lat:" + latitude + "lon:" + longitude);
        mapCenter.value.lat = latitude;
        mapCenter.value.lng = longitude;

        mapAlwaysCenter.value.lat = latitude;
        mapAlwaysCenter.value.lng = longitude;

        emit("update:currentLocation", { lat: latitude, lng: longitude });
        emit("update:locationPermission", true);
        // 获取到当前的省份
        getProvinceConfigByLatLng({ lng: longitude, lat: latitude })
          .then((res) => {
            console.log("getProvinceConfigByLatLng", res);
            let provinceId = res ? res.value : CHINA_DEFAULT_PROVINCE_ID;
            emit("update:city", provinceId);
            mapToCenterFunc(longitude, latitude, true, provinceId);
          })
          .catch((error) => {
            console.log("getProvinceConfigByLatLng error", error);
            mapToCenterFunc(
              longitude,
              latitude,
              true,
              CHINA_DEFAULT_PROVINCE_ID
            );
          });
      },
      (error) => {
        markGeoDone();
        console.error("定位失败:", error.message);
        showToast("please check location permissions");
        // alert("无法获取当前位置，请检查定位权限");
        // 默认上海
        emit("update:currentLocation", {
          lat: mapCenter.value.lat,
          lng: mapCenter.value.lng,
        });
        emit("update:locationPermission", false);
        // 中心点的文职
        setTimeout(() => {
          mapToCenterFunc(
            mapCenter.value.lng,
            mapCenter.value.lat,
            false,
            CHINA_DEFAULT_PROVINCE_ID
          );

          // 默认中心点 先设置为上海
          emit("update:city", CHINA_DEFAULT_PROVINCE_ID);
        }, 500);
      },
      geoOptions
    );
  };
  return {
    getUserLocation,
  };
};

const useMarkerEffect = (
  map,
  t,
  emit,
  mapCenter,
  mapAlwaysCenter,
  mapContainer
) => {
  const locationsObj = reactive({
    locations: [],
  });
  // 当前省份的门店标记（用于切换省份时清理）
  const dealerMarkers = [];
  // 同坐标聚合数量标签（count > 1 时显示）
  const dealerCountLabels = [];
  // 当前高亮的门店标记（列表点击时使用）
  let activeDealerMarker = null;
  // 当前高亮门店对应的数量徽标（避免绿色高亮覆盖聚合数字）
  let activeDealerCountBadge = null;
  // 当前高亮门店的数据（用于点击高亮标记时弹框）
  let activeDealerData = null;

  const clearDealerMarkers = () => {
    if (!map.value) return;
    // 清理省份门店标记
    if (dealerMarkers.length > 0) {
      dealerMarkers.forEach((m) => {
        try {
          map.value.removeOverLay(m);
        } catch (e) {}
      });
      dealerMarkers.splice(0, dealerMarkers.length);
    }
    if (dealerCountLabels.length > 0) {
      dealerCountLabels.forEach((item) => {
        try {
          map.value.removeOverLay(item.marker);
        } catch (e) {}
      });
      dealerCountLabels.splice(0, dealerCountLabels.length);
    }
    // 清理高亮标记
    if (activeDealerMarker) {
      try {
        map.value.removeOverLay(activeDealerMarker);
      } catch (e) {}
      activeDealerMarker = null;
      activeDealerData = null;
    }
    if (activeDealerCountBadge) {
      try {
        map.value.removeOverLay(activeDealerCountBadge);
      } catch (e) {}
      activeDealerCountBadge = null;
    }
    // 清理缓存位置，避免 onMarkPosition 重复累积
    locationsObj.locations.splice(0, locationsObj.locations.length);
  };

  // 生成圆形绿色数字徽标（SVG Data URL）
  const createCountBadgeDataUrl = (count) => {
    const text = String(count);
    const fontSize = text.length >= 3 ? 9 : 10;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
  <circle cx="11" cy="11" r="10" fill="#338ff0" stroke="#d8e2ef" stroke-width="2" />
  <text x="11" y="11" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="#fff">${text}</text>
</svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  /** 判断门店标记是否与给定经纬度一致（6 位小数），兼容 endLat/endLng 与 lnglat */
  const dealerPosMatchesCoord = (pos, latitude, longitude) => {
    if (pos?.id !== "dealer") return false;
    const latN = Number(latitude);
    const lngN = Number(longitude);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return false;
    const same = (a, b) =>
      Number.isFinite(a) &&
      Number.isFinite(b) &&
      a.toFixed(6) === latN.toFixed(6) &&
      b.toFixed(6) === lngN.toFixed(6);
    if (same(Number(pos?.endLatitude), Number(pos?.endLongitude))) return true;
    const ll = pos?.lnglat;
    if (ll) {
      const plat =
        typeof ll.getLat === "function"
          ? ll.getLat()
          : Number(ll.lat ?? ll.Lat);
      const plng =
        typeof ll.getLng === "function"
          ? ll.getLng()
          : Number(ll.lng ?? ll.Lng);
      if (same(Number(plat), Number(plng))) return true;
    }
    return false;
  };

  const onMarkPosition = (elementList) => {
    // 切换省份/重新加载时，先清掉上一批门店标记
    clearDealerMarkers();
    // 同坐标聚合：多个门店共用一个 marker
    const grouped = new Map();
    (elementList || []).forEach((element) => {
      const latNum = Number(element?.latitude);
      const lngNum = Number(element?.longitude);
      if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;
      const key = `${lngNum.toFixed(6)},${latNum.toFixed(6)}`;
      if (!grouped.has(key)) {
        grouped.set(key, { latNum, lngNum, dealers: [] });
      }
      grouped.get(key).dealers.push(element);
    });
    grouped.forEach((group) => {
      const first = group.dealers[0];
      locationsObj.locations.push({
        lnglat: new T.LngLat(group.lngNum, group.latNum),
        icon: new T.Icon({
          iconUrl: positionOtherIcon,
          iconSize: new T.Point(32, 32), // 设置图标的尺寸
        }),
        id: "dealer",
        fullName: first?.fullName,
        address: first?.address,
        endLatitude: first?.latitude,
        endLongitude: first?.longitude,
        groupDealers: group.dealers,
        dealerCount: group.dealers.length,
      });
    });

    locationsObj.locations.forEach((pos) => {
      const marker = new T.Marker(pos.lnglat, { icon: pos.icon });
      map.value.addOverLay(marker);
      dealerMarkers.push(marker);
      if (Number(pos.dealerCount) > 1) {
        const badgeIcon = new T.Icon({
          iconUrl: createCountBadgeDataUrl(pos.dealerCount),
          iconSize: new T.Point(22, 22),
          iconAnchor: new T.Point(4, 28),
        });
        const badgeMarker = new T.Marker(pos.lnglat, {
          icon: badgeIcon,
          offset: new T.Point(16, -16),
        });
        map.value.addOverLay(badgeMarker);
        dealerCountLabels.push({
          marker: badgeMarker,
          lat: Number(pos.endLatitude),
          lng: Number(pos.endLongitude),
        });
      }
      marker.addEventListener("click", () => {
        if (pos.id == "self") {
          showToast(t("app.home.selfplace"));
        } else {
          // 触发父容器
          emit("onMarkerClick", {
            id: pos.id,
            fullName: pos.fullName,
            address: pos.address,
            startLatitude: mapCenter.value.lat,
            startLongitude: mapCenter.value.lng,
            endLatitude: pos.endLatitude,
            endLongitude: pos.endLongitude,
            groupDealers: pos.groupDealers || [],
          });
        }
      });
    });
  };
  const getAllDistrictList = async (provinceId) => {
    // 当前所有的吧
    let param = {
      provinceIdEqual: [provinceId],
    };
    const query = [];
    Object.keys(param).forEach((key) => {
      let list = param[key];
      let innerquery;
      if (list && Array.isArray(list)) {
        innerquery = list
          .filter((item) => item)
          .map((item) => `${key}=${item}`)
          .join("&");
        if (innerquery) {
          query.push(innerquery);
        }
      }
    });
    const finalquery = query.join("&"); // 手动拼接查询字符串
    console.log("getAllDietrictList finalquery", finalquery);
    const res = await getMultiLevelDistrictApi(finalquery, null);
    if (res.error == null) {
      return res.data.list || [];
    }
    return [];
  };

  const getNearbyDealerFunc = async (latitude, longitude, provinceId) => {
    const allDistrictList = await getAllDistrictList(provinceId);
    if (allDistrictList || allDistrictList.length > 0) {
      let districtIdList = allDistrictList.map((item) => item.id);
      let param = {
        // longitude: longitude,
        // latitude: latitude,
        districtIdEqual: [districtIdList],
      };
      const query = [];
      Object.keys(param).forEach((key) => {
        let list = param[key];
        let innerquery;
        if (list && Array.isArray(list)) {
          innerquery = list
            .filter((item) => item)
            .map((item) => `${key}=${item}`)
            .join("&");
          if (innerquery) {
            query.push(innerquery);
          }
        } else {
          query.push(`${key}=${list}`);
        }
      });
      const finalquery = query.join("&"); // 手动拼接查询字符串
      console.log("finalquery", finalquery);
      // 这个是老的获取经销商的列表  先暂时隐藏掉吧
      // const res = await getDealerListApi(param);
      const res = await getOpenRetailerListApi(finalquery, null);
      if (res.error == null) {
        let dealerList = res.data.list;
        // todo 做一下处理
        // dealerList.forEach((item, index) => {
        //   if (item.address == "解放东路225号") {
        //     item.latitude = "31.8944639";
        //     item.longitude = "121.2010698";
        //   } else if (item.address == "丹阳路") {
        //     item.latitude = "32.204402";
        //     item.longitude = "119.452753";
        //   } else {
        //     if (index == 0) {
        //       item.latitude = "31.2185";
        //       item.longitude = "121.5512";
        //     }
        //     if (index == 1) {
        //       item.latitude = "31.2310";
        //       item.longitude = "121.4770";
        //     }
        //     if (index == 2) {
        //       item.latitude = "31.1732";
        //       item.longitude = "121.3985";
        //     }
        //     if (index == 3) {
        //       item.latitude = "31.2721";
        //       item.longitude = "121.4503";
        //     }
        //   }
        // });

        // 直线距离（km 整数）
        (dealerList || []).forEach((it) => {
          it.fullAddress =
            it?.district?.city?.province?.localizedFullName +
            it?.district?.city?.localizedFullName +
            it?.district?.localizedFullName +
            it.address;
          it.distancekm = calcDistanceKm(
            { lat: mapAlwaysCenter.value.lat, lng: mapAlwaysCenter.value.lng },
            { lat: it.latitude, lng: it.longitude }
          );
          if (!it.latitude || !it.longitude) {
            it.showdistancekm = false;
          } else {
            it.showdistancekm = true;
          }
        });

        // 计算路线里程（km 整数）：优先天地图驾车距离，拿不到则退化为直线距离
        // await Promise.all(
        //   (dealerList || []).map(async (item) => {
        //     const km = await calcTdDrivingDistanceKm(
        //       {
        //         lat: mapAlwaysCenter.value.lat,
        //         lng: mapAlwaysCenter.value.lng,
        //       },
        //       { lat: item.latitude, lng: item.longitude },
        //       map.value
        //     );
        //     console.log("dealerlist one", km);
        //     item.distancekm = km;
        //   })
        // );
        dealerList.sort((a, b) => {
          const da = Number(a?.distancekm ?? Infinity);
          const db = Number(b?.distancekm ?? Infinity);
          return da - db;
        });

        console.log("dealerlistpp", dealerList);
        // dealerList.forEach(item=>{
        //   if(item.fullName=='嘉实多汽车贴膜京东自营旗舰店'){
        //     // item.latitude = "31.21077";
        //     // item.longitude = "121.41821";
        //     item.latitude = "31.2104";
        //     item.longitude = "121.41819";
        //   }
        // })
        emit("onDealerList", dealerList);
        onMarkPosition(dealerList);
        return Array.isArray(dealerList) ? dealerList : [];
      }
    } else {
      showToast("Failed to get district information");
    }
    return [];
  };

  // 列表点击某个门店时，高亮该门店：不清除“我的位置”图标
  const focusOnDealer = ({ longitude, latitude, dealer }) => {
    if (!map.value || longitude == null || latitude == null) return;
    const point = new T.LngLat(longitude, latitude);
    // 地图平移到该点，放大一级，体验更清晰
    map.value.centerAndZoom(point, 13);
    activeDealerData = dealer || null;

    // 先解析当前点是否为聚合点（与列表传入的经纬度对齐，兼容 lnglat）
    const hitGrouped = (locationsObj.locations || []).find((pos) =>
      dealerPosMatchesCoord(pos, latitude, longitude)
    );
    const dealerCount = Number(hitGrouped?.dealerCount || 0);

    // 移除之前高亮的门店标记
    if (activeDealerMarker) {
      map.value.removeOverLay(activeDealerMarker);
      activeDealerMarker = null;
    }
    // if (activeDealerCountBadge) {
    //   map.value.removeOverLay(activeDealerCountBadge);
    //   activeDealerCountBadge = null;
    // }

    // 仅在确认为聚合点时才移除原数字徽标，并会立刻补高亮态徽标；避免误删导致数字消失
    const idx = dealerCountLabels.findIndex((item) => {
      const latNum = Number(item?.lat);
      const lngNum = Number(item?.lng);
      return (
        Number.isFinite(latNum) &&
        Number.isFinite(lngNum) &&
        latNum.toFixed(6) === Number(latitude).toFixed(6) &&
        lngNum.toFixed(6) === Number(longitude).toFixed(6)
      );
    });
    if (idx > -1 && dealerCount > 1) {
      try {
        map.value.removeOverLay(dealerCountLabels[idx].marker);
      } catch (e) {}
      dealerCountLabels.splice(idx, 1);
    }

    // 使用 position.png 图标高亮当前门店
    const icon = new T.Icon({
      iconUrl: positionIcon,
      iconSize: new T.Point(32, 32),
    });
    activeDealerMarker = new T.Marker(point, { icon });
    map.value.addOverLay(activeDealerMarker);

    // 若该坐标是聚合点（count > 1），在高亮态补一个数量徽标（与默认徽标同样偏移，避免被绿钉挡住）
    if (dealerCount > 1) {
      const badgeIcon = new T.Icon({
        iconUrl: createCountBadgeDataUrl(dealerCount),
        iconSize: new T.Point(22, 22),
        iconAnchor: new T.Point(4, 28),
      });
      activeDealerCountBadge = new T.Marker(point, {
        icon: badgeIcon,
        offset: new T.Point(16, -16),
      });
      map.value.addOverLay(activeDealerCountBadge);
    }

    // 让高亮标记可点击：与红色门店标记一样触发父组件弹框
    activeDealerMarker.addEventListener("click", () => {
      const endLat = latitude;
      const endLng = longitude;
      emit("onMarkerClick", {
        id: "dealer",
        fullName: activeDealerData?.fullName,
        address: activeDealerData?.address || activeDealerData?.fullAddress,
        startLatitude: mapCenter.value.lat,
        startLongitude: mapCenter.value.lng,
        endLatitude: endLat,
        endLongitude: endLng,
        groupDealers: hitGrouped?.groupDealers || [],
      });
    });
  };

  // 回到中心点
  const onGoMapCenter = ({ longitude, latitude }) => {
    mapCenter.value.lat = latitude;
    mapCenter.value.lng = longitude;
    console.log("onGoMapCenter", longitude, latitude);
    map.value.clearOverLays();
    locationsObj.locations.splice(0, locationsObj.locations.length);
    map.value.centerAndZoom(new T.LngLat(longitude, latitude), 11); // 设置中心点为北京
    locationsObj.locations.push({
      lnglat: new T.LngLat(longitude, latitude),
      icon: new T.Icon({
        iconUrl: myLocationIcon,
        iconSize: new T.Point(32, 32), // 设置图标的尺寸
      }),
      id: "self",
    });
    locationsObj.locations.forEach((pos) => {
      const marker = new T.Marker(pos.lnglat, { icon: pos.icon });
      map.value.addOverLay(marker);
      marker.addEventListener("click", () => {
        showToast(t("app.home.selfplace"));
      });
    });
    // 兜底：不传省份时，按默认省份拉取门店
    getNearbyDealerFunc(latitude, longitude, CHINA_DEFAULT_PROVINCE_ID);
  };
  const { locations } = toRefs(locationsObj);
  return {
    locations,
    onMarkPosition,
    onGoMapCenter,
    getNearbyDealerFunc,
    focusOnDealer,
  };
};

const initMapEffect = (
  map,
  mapContainer,
  getUserLocation,
  locations,
  mapCenter,
  mainMap,
  getNearbyDealerFunc,
  defaultZoom,
  mapLang
) => {
  // 初始化天地图
  const initMap = () => {
    if (!window.T) {
      // 安全兜底：若此时 T 还没挂上，稍后再试一次
      setTimeout(() => {
        if (!window.T) {
          showToast("T is not defined. Ensure the TianDiTu API is loaded.");
        } else {
          initMap();
        }
      }, 300);
      return;
    }

    map.value = new T.Map(mapContainer.value); // 创建地图实例，并挂载到 ref 指向的容器
    locations.value.splice(0, locations.value.length);
    getUserLocation((longitude, latitude, allowlocation, provinceId) => {
      const point = new T.LngLat(longitude, latitude);
      map.value.centerAndZoom(point, defaultZoom); // 设置地图中心和缩放级别
      // 兼容境外初次加载偶发白屏：初始化后再触发一次重绘
      setTimeout(() => {
        try {
          map.value?.centerAndZoom(point, defaultZoom);
        } catch (e) {}
      }, 120);
      map.value.enableDrag();
      // if (allowlocation) {
      //   // 当前允许定位的话 就会有当前的定位
      // }
      locations.value.push({
        lnglat: new T.LngLat(longitude, latitude),
        icon: new T.Icon({
          iconUrl: myLocationIcon,
          iconSize: new T.Point(32, 32), // 设置图标的尺寸
        }),
      });

      locations.value.forEach((pos) => {
        const marker = new T.Marker(pos.lnglat, { icon: pos.icon });
        map.value.addOverLay(marker);
      });

      getNearbyDealerFunc(latitude, longitude, provinceId);
    }, mapCenter);
  };

  const onLoadMap = () => {
    const needLang = mapLang;

    const getMarkedLoadedLang = () => {
      if (typeof window.__TIANDITU_API_LANG__ === "string") {
        return window.__TIANDITU_API_LANG__;
      }
      const el = document.querySelector(
        'script[src*="api.tianditu.gov.cn/api"]'
      );
      if (el?.src) return parseTiandituLangFromScriptSrc(el.src);
      // 旧页面曾加载过无 lang 的脚本：视为中文包
      if (window.T) return "zh_cn";
      return null;
    };

    const loadedMark = getMarkedLoadedLang();
    // 已加载且语言一致：直接初始化（避免重复插 script）
    if (
      window.T &&
      loadedMark != null &&
      tdApiLangMatches(loadedMark, needLang)
    ) {
      initMap();
      return;
    }

    // 语言不一致、或曾加载默认中文包但需要英文等：必须卸脚本并重拉，否则 URL 上的 lang 永远不生效
    if (
      window.T ||
      document.querySelector('script[src*="api.tianditu.gov.cn/api"]')
    ) {
      clearTiandituRuntime();
    }

    const existing = document.querySelector(
      'script[src*="api.tianditu.gov.cn/api"]'
    );
    if (existing) {
      const srcLang = parseTiandituLangFromScriptSrc(existing.src);
      if (tdApiLangMatches(srcLang, needLang)) {
        if (!existing.__tdInitBound) {
          existing.__tdInitBound = true;
          existing.addEventListener("load", () => {
            window.__TIANDITU_API_LANG__ = needLang;
            initMap();
          });
        }
        return;
      }
      existing.remove();
    }

    const script = document.createElement("script");
    // 境外用 en；若仍显示中文，可试官方其它取值如 en_US（以天地图文档为准）
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&lang=${encodeURIComponent(
      needLang
    )}&tk=${TD_MAP_KEY}`;
    script.async = true;
    script.addEventListener("load", () => {
      window.__TIANDITU_API_LANG__ = needLang;
      initMap();
    });
    script.addEventListener("error", () => {
      showToast("Tianditu script load error");
    });
    document.body.appendChild(script);
  };
  const onDestoryMap = () => {
    // if (mainMap.value) {
    //   mainMap.value.removeChild(mapContainer.value);
    //   const mapContainerEl = document.createElement("div");
    //   mapContainerEl.id = "mapContainer";
    //   mapContainerEl.classList.add("mapwrapper");
    //   mainMap.value.appendChild(mapContainerEl);
    // }
    mainMap.value = false;
    if (map.value) {
      map.value.clearOverLays();
      mapContainer.value = null;
      //   mapContainer.value.innerHTML = "";
      map.value = null;
    }
  };
  nextTick(() => {
    onLoadMap();
  });

  const onReload = () => {
    onDestoryMap();
    clearTiandituRuntime();
    setTimeout(() => {
      mainMap.value = true;
      nextTick(() => {
        onLoadMap();
      });
    }, 200);
  };
  return { onReload };
};

export default {
  name: "TianDiTuMap",
  emits: [
    "onMarkerClick",
    "onDealerList",
    "update:city",
    "update:currentLocation",
    "update:locationPermission",
  ],
  props: {
    city: { type: String, default: CHINA_DEFAULT_PROVINCE_ID },
    locationPermission: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n(); // 获取 t 方法
    let map = ref(null);

    const mapCenter = ref(
      isInChina()
        ? // { lat: 31.208457, lng: 121.423027 }
          CHINA_DEFAULT_LOCATION
        : { lat: 3.139, lng: 101.6869 }
    ); // 默认中心点
    const mapAlwaysCenter = ref(
      isInChina()
        ? // { lat: 31.208457, lng: 121.423027 }
          CHINA_DEFAULT_LOCATION
        : { lat: 3.139, lng: 101.6869 }
    ); // 始终中心点
    // 境外首屏用稍小缩放，避免初次白屏（手动缩放后才出图）
    const defaultZoom = isInChina() ? 11 : 6;
    // 国内中文，境外英文
    const mapLang = isInChina() ? "zh_cn" : "en";
    const mapContainer = ref(null); // 用来引用地图容器的 DOM 元素
    const mainMap = ref(true);

    const { getUserLocation } = useMapPositionEffect(emit, mapAlwaysCenter, t);
    const {
      locations,
      onMarkPosition,
      onGoMapCenter,
      getNearbyDealerFunc,
      focusOnDealer,
    } = useMarkerEffect(map, t, emit, mapCenter, mapAlwaysCenter, mapContainer);
    const { onReload } = initMapEffect(
      map,
      mapContainer,
      getUserLocation,
      locations,
      mapCenter,
      mainMap,
      getNearbyDealerFunc,
      defaultZoom,
      mapLang
    );

    const getProvinceTextById = (provinceId) => {
      const list = CHINA_ALL_PROVINCE || [];
      const hit = list.find((x) => String(x.value) === String(provinceId));
      return hit?.text || "";
    };

    const getZoomForProvince = () => {
      // 统一给一个相对“看得全”的平均缩放，避免依赖中文省份文案（方便后续做国外版本）
      return 7;
    };

    const flyToProvince = async (provinceId) => {
      if (!provinceId || provinceId === "ALL") return;
      if (!map.value || !window.T) return;

      const provinceText = getProvinceTextById(provinceId);
      if (!provinceText) return;

      try {
        const res = await getTdMapLocation(provinceText);
        // 兼容两种返回结构：res 或 res.data
        const data = res?.data || res;
        const status = data?.status ?? data?.data?.status;
        if (status !== "0" && status !== 0) return;

        const loc = data?.location || data?.data?.location;
        const lat = Number(loc?.lat);
        const lon = Number(loc?.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

        // 先尝试拉取该省门店并渲染标记
        const dealerList = await getNearbyDealerFunc(lat, lon, provinceId);

        // 若当前省份下有门店：直接按列表第一个门店定位（移动端与旧逻辑保持一致）
        if (Array.isArray(dealerList) && dealerList.length > 0) {
          const first = dealerList[0];
          console.log("first", first);
          // 第一个元素的经纬度必须存在才行
          if (first && first.latitude && first.longitude) {
            const dLat = Number(
              first?.latitude ?? first?.endLatitude ?? first?.lat
            );
            const dLng = Number(
              first?.longitude ?? first?.endLongitude ?? first?.lng
            );
            if (Number.isFinite(dLat) && Number.isFinite(dLng)) {
              map.value.centerAndZoom(new T.LngLat(dLng, dLat), 13);
              return;
            }
          }
        }

        // 若无门店：按省份中心 + 缩放（尽量看全省）
        const zoom = getZoomForProvince();
        map.value.centerAndZoom(new T.LngLat(lon, lat), zoom);
      } catch (e) {
        // 静默失败即可，不影响其他功能
      }
    };

    // 用户手动切换省份后：把地图挪到对应省份位置并适当缩放
    watch(
      () => props.city,
      (v) => {
        flyToProvince(v);
      }
    );

    return {
      mainMap,
      mapContainer, // 返回 ref 供模板使用
      onMarkPosition,
      onGoMapCenter,
      onFocusDealer: focusOnDealer,
      onReload,
    };
  },
};
</script>

<style lang="scss" scoped>
.mapwrapper {
  height: 100vh;
  width: 100%;
}
</style>
